import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { workQueueApi, type CallCenterItem, type CallCenterQueue } from '../api/workQueue'
import { callOutcomesApi, leadsApi, type CallOutcome, type CreateCallAttemptRequest, type Lead } from '../api/leads'
import { appointmentsApi } from '../api/operations'
import { LeadCallCenterPanel } from '../components/LeadCallCenterPanel'
import {
  FormGrid,
  Button, ButtonLink, Callout, Card, CardContent, CardHeader, DataTable, EmptyState, KeyValueList, LoadingState, Page, PageHeader,
  Select, StatusPill, TextField, type PillTone,
} from '../components/ui'

// F-051 (REQ-114-001, REQ-113-001): the operator's workstation. It shows the leads the operator has to
// work, in the REQ-114 order decided by the server, and "Prossimo lead" opens the first one. The call
// itself can be recorded from the lead page (attempt modal) or, since F-091, with the quick outcome buttons
// under the next lead (one attempt per click, same endpoint as the modal). F-110 (REQ-113-001): the same card also
// has "Qualifica", "Non interessato" and an inline appointment form, so the whole call can be closed without
// leaving the workstation.

const RANK_LABELS: Record<number, { label: string; color: PillTone }> = {
  0: { label: 'Appena arrivato', color: 'primary' },
  1: { label: 'Richiamata', color: 'warning' },
  2: { label: 'Caldo', color: 'error' },
  3: { label: 'Ritentativo', color: 'info' },
  4: { label: 'Da lavorare', color: 'neutral' },
  5: { label: 'Tentativi esauriti', color: 'neutral' },
}

const CONFIRM_BADGE: { label: string; color: PillTone } = { label: 'Da confermare', color: 'warning' }

const TEMPERATURE_LABELS: Record<string, string> = {
  cold: 'Freddo',
  warm: 'Tiepido',
  hot: 'Caldo',
  very_hot: 'Molto caldo',
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function RankBadge({ rank, type }: { rank: number; type?: string }) {
  const r = type === 'appointment_da_confermare'
    ? CONFIRM_BADGE
    : (RANK_LABELS[rank] ?? { label: 'Da lavorare', color: 'neutral' as PillTone })
  return <StatusPill tone={r.color}>{r.label}</StatusPill>
}

function NextLeadCard({ next }: { next: CallCenterItem }) {
  return (
    <Card variant="outlined" padding="none" className="mb-3">
      <CardHeader title="Prossimo lead" actions={<RankBadge rank={next.rank} type={next.type} />} />
      <CardContent>
        <h2 className="md-typescale-title-large mb-1">{next.leadName || `Lead #${next.leadId}`}</h2>
        <div className="mb-2">
          {next.phone ? (
            next.phone.includes('*') ? (
              <span className="md-typescale-title-medium">{next.phone} <Link to={`/leads/${next.leadId}`}>Apri il lead per vedere il numero</Link></span>
            ) : <a href={`tel:${next.phone}`} className="text-decoration-none md-typescale-title-medium">{next.phone}</a>
          ) : (
            <span className="text-muted">Nessun numero di telefono</span>
          )}
        </div>
        <KeyValueList columns={2} className="md-typescale-body-small mb-3"
          items={[
            { label: 'Fonte', value: <>{next.sourceLabel ?? '—'}</> },
            { label: 'Prodotto', value: <>{next.product ?? '—'}</> },
            { label: 'Settore e campagna', value: <>{[next.sector, next.campaign].filter(Boolean).join(' · ') || '—'}</> },
            { label: 'Temperatura', value: <>{next.temperature ? (TEMPERATURE_LABELS[next.temperature] ?? next.temperature) : '—'}</> },
            { label: 'Punteggio', value: <>{next.score ?? '—'}</> },
            { label: 'Stato', value: <>{next.operationalState?.label ?? '—'}</> },
            { label: 'Tentativi', value: <>{next.attempts}</> },
            {
              label: 'Ultimo esito',
              value: (
                <>
                  {next.lastOutcomeLabel
                    ? `${next.lastOutcomeLabel}${next.lastAttemptAt ? ` (${fmtDateTime(next.lastAttemptAt)})` : ''}`
                    : 'Mai contattato'}
                </>
              ),
            },
            ...(next.dueAt ? [
              { label: 'Scadenza', value: <>{fmtDateTime(next.dueAt)}</> },
            ] : []),
          ]}
        />
        <ButtonLink to={`/leads/${next.leadId}`} variant="filled" icon="call">
          Apri il lead e registra la chiamata
        </ButtonLink>
      </CardContent>
    </Card>
  )
}

/** F-091: gli esiti attivi come pulsanti. L'esito di richiamo chiede la data (opzionale) del ricontatto. */
function isCallbackOutcome(o: CallOutcome): boolean {
  return o.code.toUpperCase() === 'RICHIAMARE' || /richiam/i.test(o.label)
}

/** F-110: l'esito configurato "non interessato", se esiste tra quelli attivi. */
function isNotInterestedOutcome(o: CallOutcome): boolean {
  return o.code.toUpperCase() === 'NON_INTERESSATO' || /non interessat/i.test(o.label)
}

const NOT_INTERESTED_LOST_REASON = 'Non interessato'

function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function QuickOutcomes({ leadId, leadName, sector, phone, onDone }: {
  leadId: number
  leadName: string | null
  sector: string | null
  phone: string | null
  onDone: () => void
}) {
  const [outcomes, setOutcomes] = useState<CallOutcome[]>([])
  const [callbackFor, setCallbackFor] = useState<CallOutcome | null>(null)
  const [callbackAt, setCallbackAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [showAppointment, setShowAppointment] = useState(false)
  const [apptStart, setApptStart] = useState('')
  const [apptMinutes, setApptMinutes] = useState('60')
  const [apptTitle, setApptTitle] = useState('')
  const [apptPlace, setApptPlace] = useState('')

  useEffect(() => {
    let alive = true
    callOutcomesApi.list(true)
      .then(r => { if (alive) setOutcomes([...r].sort((a, b) => a.sortOrder - b.sortOrder)) })
      .catch(() => { if (alive) setOutcomes([]) })
    return () => { alive = false }
  }, [])

  // Cambiando lead si riparte da zero.
  useEffect(() => {
    setCallbackFor(null)
    setCallbackAt('')
    setError(null)
    setNotice(null)
    setShowAppointment(false)
    setApptStart('')
    setApptTitle('')
    setApptPlace('')
  }, [leadId])

  /** F-110: "Qualifica" = stato Qualified. Da New si passa prima da Contacted (il grafo degli stati lo impone). */
  async function qualify() {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const lead = await leadsApi.get(leadId) as Lead & { availableStatusTransitions?: string[] }
      if ((lead.status ?? '').toLowerCase() === 'qualified') {
        setNotice('Il lead è già qualificato.')
        return
      }
      const reachable = (lead.availableStatusTransitions ?? []).map(t => t.toLowerCase())
      if (!reachable.includes('qualified') && reachable.includes('contacted')) {
        await leadsApi.updateStatus(leadId, 'contacted')
      }
      await leadsApi.updateStatus(leadId, 'qualified')
      setNotice('Lead qualificato.')
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nella qualificazione del lead')
    } finally {
      setBusy(false)
    }
  }

  /** F-110: "Non interessato" = l'esito configurato, altrimenti il lead passa a Perso con il motivo. */
  async function notInterested() {
    const outcome = outcomes.find(isNotInterestedOutcome)
    if (outcome) {
      await record(outcome)
      return
    }
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await leadsApi.updateStatus(leadId, 'lost', NOT_INTERESTED_LOST_REASON)
      setNotice('Lead segnato come perso: non interessato.')
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setBusy(false)
    }
  }

  function openAppointment() {
    if (!showAppointment) {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      d.setHours(10, 0, 0, 0)
      setApptStart(toLocalInput(d))
      setApptTitle(`Appuntamento con ${leadName || `lead #${leadId}`}`)
    }
    setShowAppointment(v => !v)
  }

  async function saveAppointment() {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const start = new Date(apptStart)
      if (Number.isNaN(start.getTime())) throw new Error('Indica data e ora dell\u2019appuntamento.')
      const end = new Date(start.getTime() + Number(apptMinutes) * 60_000)
      await appointmentsApi.create({
        title: apptTitle.trim() || `Appuntamento con ${leadName || `lead #${leadId}`}`,
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        leadId,
        ...(apptPlace.trim() ? { location: apptPlace.trim() } : {}),
        ...(sector ? { sector } : {}),
      })
      setShowAppointment(false)
      setNotice('Appuntamento fissato.')
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio dell\u2019appuntamento')
    } finally {
      setBusy(false)
    }
  }

  async function record(outcome: CallOutcome, when?: string) {
    setBusy(true)
    setError(null)
    try {
      const req: CreateCallAttemptRequest = { outcomeId: outcome.id }
      if (phone) req.phoneNumberCalled = phone
      if (when) req.callbackScheduledAt = new Date(when).toISOString()
      await leadsApi.createCallAttempt(leadId, req)
      setCallbackFor(null)
      setCallbackAt('')
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio dell\'esito')
    } finally {
      setBusy(false)
    }
  }

  if (outcomes.length === 0) return null

  return (
    <Card variant="outlined" padding="none" className="mb-3">
      <CardHeader title="Esito della chiamata" />
      <CardContent>
        <div className="d-flex flex-wrap gap-2" role="group" aria-label="Esiti rapidi">
          {outcomes.filter(o => !isNotInterestedOutcome(o)).map(o => (
            <Button
              key={o.id}
              type="button"
              size="sm"
              variant={callbackFor?.id === o.id ? 'filled' : 'outlined'}
              disabled={busy}
              onClick={() => (isCallbackOutcome(o) ? setCallbackFor(o) : void record(o))}
            >
              {o.label}
            </Button>
          ))}
          <Button type="button" size="sm" variant="outlined" disabled={busy} onClick={() => void notInterested()}>
            Non interessato
          </Button>
          <Button type="button" size="sm" variant="filled" disabled={busy} onClick={() => void qualify()}>
            Qualifica
          </Button>
          <Button
            type="button"
            size="sm"
            variant={showAppointment ? 'tonal' : 'outlined'}
            disabled={busy}
            aria-expanded={showAppointment}
            onClick={openAppointment}
          >
            Fissa appuntamento
          </Button>
        </div>
        {showAppointment && (
          <FormGrid columns={4} className="mt-3 md-form-grid--end">
            <div>
              <TextField id="ws-appt-title" label="Titolo" value={apptTitle}
                onChange={e => setApptTitle(e.target.value)} disabled={busy} />
            </div>
            <div>
              <TextField id="ws-appt-start" label="Data e ora" type="datetime-local" value={apptStart}
                onChange={e => setApptStart(e.target.value)} disabled={busy} />
            </div>
            <div>
              <Select id="ws-appt-len" label="Durata" value={apptMinutes}
                onChange={e => setApptMinutes(e.target.value)} disabled={busy}>
                <option value="30">30 minuti</option>
                <option value="60">1 ora</option>
                <option value="90">1 ora e 30</option>
                <option value="120">2 ore</option>
              </Select>
            </div>
            <div>
              <TextField id="ws-appt-place" label="Luogo (facoltativo)" value={apptPlace}
                onChange={e => setApptPlace(e.target.value)} disabled={busy} />
            </div>
            <div className="md-span-full d-flex gap-2">
              <Button type="button" size="sm" variant="filled" disabled={busy || !apptStart} onClick={() => void saveAppointment()}>
                {busy ? 'Salvataggio...' : 'Fissa l\u2019appuntamento'}
              </Button>
              <Button type="button" size="sm" variant="text" disabled={busy} onClick={() => setShowAppointment(false)}>
                Annulla
              </Button>
            </div>
          </FormGrid>
        )}
        {callbackFor && (
          <div className="mt-3 d-flex flex-wrap align-items-end gap-2">
            <div>
              <TextField
                id="ws-callback-at"
                label="Quando richiamare (facoltativo)"
                type="datetime-local"
                value={callbackAt}
                onChange={e => setCallbackAt(e.target.value)}
                disabled={busy}
              />
            </div>
            <Button type="button" size="sm" variant="filled" disabled={busy} onClick={() => void record(callbackFor, callbackAt || undefined)}>
              {busy ? 'Salvataggio...' : `Registra "${callbackFor.label}"`}
            </Button>
            <Button type="button" size="sm" variant="text" disabled={busy} onClick={() => setCallbackFor(null)}>
              Annulla
            </Button>
          </div>
        )}
        {notice && <Callout tone="success" role="status" className="mt-2">{notice}</Callout>}
        {error && <Callout tone="error" role="alert" className="mt-2">{error}</Callout>}
      </CardContent>
    </Card>
  )
}

export function WorkstationPage() {
  const [data, setData] = useState<CallCenterQueue | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await workQueueApi.callCenter())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento della coda')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const items = data?.items ?? []

  return (
    <Page>
      <PageHeader
        title="Postazione operatore"
        icon="headset_mic"
        actions={
          <Button type="button" variant="outlined" onClick={() => void load()} disabled={loading}>
            Aggiorna la coda
          </Button>
        }
      />

      <Callout tone="info" role="note">
        Qui vedi i lead che devi lavorare, nell&apos;ordine: appena arrivati, richiamate dovute, lead caldi,
        ritentativi, lead più vecchi; nello stesso gruppo viene prima il lead con il punteggio più alto
        (il punteggio non è una probabilità di chiusura). Compaiono anche gli appuntamenti da confermare nelle prossime
        {data ? ` ${data.provisional.confirmWindowHours}` : ''} ore (valore provvisorio). Sotto il prossimo lead trovi lo script, il questionario e i pulsanti per registrare l&apos;esito della chiamata; puoi anche qualificare il lead, segnarlo come non interessato o fissare l&apos;appuntamento senza lasciare la pagina.
        {data?.provisional.retrySchedule && (
          <> Gli intervalli dei ritentativi (subito, dopo 3 ore, il giorno dopo, dopo 2 giorni, dopo 7 giorni; massimo {data.provisional.maxAttempts} tentativi)
            e la finestra di &quot;appena arrivato&quot; ({data.provisional.recentWindowHours} ore) sono valori provvisori.</>
        )}
        {' '}Non c&apos;è una presa in carico: se due operatori vedono lo stesso lead, entrambi se lo vedono proporre.
      </Callout>

      {error && (
        <Callout
          tone="error"
          role="alert"
          action={<Button type="button" variant="text" onClick={() => void load()}>Riprova</Button>}
        >
          {error}
        </Callout>
      )}

      <div aria-busy={loading}>
        {loading ? (
          <LoadingState label="Caricamento della coda..." />
        ) : error || !data ? null : (
          <>
            {data.next ? (
              <>
                <NextLeadCard next={data.next} />
                {data.next.leadId !== null && (
                  <>
                    <QuickOutcomes
                      leadId={data.next.leadId}
                      leadName={data.next.leadName}
                      sector={data.next.sector}
                      phone={data.next.phone}
                      onDone={() => void load()}
                    />
                    <LeadCallCenterPanel leadId={data.next.leadId} compact />
                  </>
                )}
              </>
            ) : (
              <EmptyState
                icon="task_alt"
                title="Nessun lead da lavorare in questo momento."
                description="Compaiono qui i lead nuovi assegnati a te (o ai tuoi collaboratori) mai contattati, le richiamate dovute e i ritentativi. Un lead non assegnato a nessuno non è visibile qui."
              />
            )}

            {items.length > 0 && (
              <Card variant="outlined" padding="none">
                <CardHeader
                  title="Coda di lavoro"
                  actions={
                    <span className="text-muted md-typescale-body-small" aria-live="polite">
                      {data.hasMore ? `Primi ${data.count} su ${data.total}` : `${data.total} elementi`}
                    </span>
                  }
                />
                <DataTable variant="plain" stickyHeader caption="Coda di lavoro del call center">
                  <thead>
                    <tr>
                      <th scope="col">Priorità</th>
                      <th scope="col">Lead</th>
                      <th scope="col" className="md-num">Punteggio</th>
                      <th scope="col">Stato</th>
                      <th scope="col">Telefono</th>
                      <th scope="col">Fonte</th>
                      <th scope="col" className="md-num">Tentativi</th>
                      <th scope="col">Ultimo esito</th>
                      <th scope="col">Scadenza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(i => (
                      <tr key={`${i.entityType}-${i.entityId}`}>
                        <td><RankBadge rank={i.rank} type={i.type} /></td>
                        <td>
                          {i.leadId ? (
                            <Link to={`/leads/${i.leadId}`} className="text-decoration-none md-typescale-emphasized-body-medium">
                              {i.leadName || i.title}
                            </Link>
                          ) : (
                            <span>{i.title}</span>
                          )}
                          {i.entityType !== 'task' && <div className="md-typescale-body-small text-muted">{i.title}</div>}
                        </td>
                        <td className="md-num">{i.score ?? '—'}</td>
                        <td>{i.operationalState?.label ?? '—'}</td>
                        <td>{i.phone ?? '—'}</td>
                        <td>{i.sourceLabel ?? '—'}</td>
                        <td className="md-num">{i.entityType === 'lead' ? i.attempts : '—'}</td>
                        <td>{i.lastOutcomeLabel ?? '—'}</td>
                        <td className="text-nowrap">{i.dueAt ? fmtDateTime(i.dueAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </Card>
            )}
          </>
        )}
      </div>
    </Page>
  )
}
